import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { AdviserPanel } from "../components/onboarding/AdviserPanel";
import { GuidedTour } from "../components/GuidedTour";
import { BillingCycleToggle, annualAmount } from "../components/BillingCycleToggle";
import { toast } from "sonner";
import { formatSgd } from "../lib/utils";
import { apiErr } from "../lib/utils";
import {
  Check, Circle, ShieldCheck, Clock, BadgeCheck, Database, Target, Gauge, Mail,
  Sparkles, Compass, ArrowUpRight, Minus, Plus, AlertCircle, CircleCheck,
  Copy, AlertTriangle, Code2, ExternalLink, FastForward, CreditCard,
} from "lucide-react";

export default function OnboardingDashboard() {
  const [data, setData] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const [d, n] = await Promise.all([
        api.get("/onboarding/dashboard"),
        api.get("/onboarding/notifications"),
      ]);
      setData(d.data);
      setNotifs(n.data.notifications || []);
    } catch (e) {
      // non self-service tenants may not have onboarding data
      setData({ unavailable: true });
    }
  };

  const pollPayment = async (sessionId, attempt = 0) => {
    if (attempt >= 6) { toast.error("Payment status check timed out. Please refresh."); return; }
    try {
      const { data: s } = await api.get(`/payments/checkout/status/${sessionId}`);
      if (s.payment_status === "paid") { toast.success("Payment successful — your subscription is active!"); load(); return; }
      if (s.status === "expired") { toast.error("Payment session expired."); return; }
      setTimeout(() => pollPayment(sessionId, attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => pollPayment(sessionId, attempt + 1), 2000);
    }
  };

  useEffect(() => {
    load();
    api.get("/onboarding/packages").then((r) => setPackages(r.data.packages || [])).catch(() => {});
    if (params.get("tour") === "1") {
      setTourOpen(true);
      params.delete("tour");
      setParams(params, { replace: true });
    }
    const sessionId = params.get("session_id");
    if (sessionId) {
      toast.info("Confirming your payment…");
      pollPayment(sessionId);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return <div className="p-10 text-muted-foreground font-mono text-sm" data-testid="onboarding-dashboard-loading">Loading setup centre…</div>;
  }

  if (data.unavailable) {
    return (
      <div className="p-10 max-w-xl" data-testid="onboarding-dashboard-empty">
        <h1 className="font-display text-2xl font-bold">Setup Centre</h1>
        <p className="text-muted-foreground text-sm mt-2">
          This workspace wasn't provisioned through self-service onboarding, so there's no onboarding telemetry to show.
          Explore the platform modules from the sidebar.
        </p>
      </div>
    );
  }

  const sc = data.setup_completion;
  const trial = data.trial;
  const v = data.verification;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="onboarding-dashboard">
      <GuidedTour open={tourOpen} onClose={() => setTourOpen(false)} />

      {data.tenant.read_only && (
        <div className="card-flat bg-red-500/10 border-red-500/40 p-4 mb-5 flex items-center gap-3" data-testid="readonly-banner">
          <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-sm">
              {data.trial?.status === "canceled"
                ? "Your subscription has ended — workspace is read-only"
                : "Your trial has expired — workspace is read-only"}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">Your data is safe. Reactivate a paid plan to restore full access.</div>
          </div>
          <Button variant="primary" size="sm" onClick={() => setUpgradeOpen(true)} data-testid="banner-upgrade-btn" className="gap-1.5 shrink-0">
            <CreditCard className="h-4 w-4" /> {data.trial?.status === "canceled" ? "Reactivate" : "Upgrade now"}
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Setup Centre</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1" data-testid="dashboard-tenant-name">{data.tenant.name}</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {data.tenant.entity_category && <Badge variant="outline">{data.tenant.entity_category}</Badge>}
            {data.tenant.regulatory_tier && <Badge variant="warning">{data.tenant.regulatory_tier}</Badge>}
            {data.tenant.industry_name && <Badge variant="default">{data.tenant.industry_name}</Badge>}
          </div>
        </div>
        <Button variant="outline" onClick={() => setTourOpen(true)} className="gap-2 shrink-0" data-testid="take-tour-btn">
          <Compass className="h-4 w-4" /> Take the tour
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* LEFT 2/3 */}
        <div className="lg:col-span-2 space-y-5">
          {/* Setup completion */}
          <Section icon={Gauge} title="Setup completion" testid="setup-completion">
            <div className="flex items-center gap-4 mb-4">
              <div className="font-mono text-5xl font-bold" data-testid="setup-score">{sc.score}%</div>
              <div className="flex-1">
                <Progress value={sc.score} className="h-2 mb-1.5" />
                <div className="text-xs text-muted-foreground">{sc.completed} of {sc.total} tasks done · Next: <span className="text-foreground">{sc.next_action}</span></div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {sc.checklist.map((c) => (
                <div key={c.key} className="flex items-center gap-2.5 text-sm" data-testid={`checklist-${c.key}`}>
                  {c.done
                    ? <CircleCheck className="h-4 w-4 text-accent shrink-0" />
                    : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <span className={c.done ? "" : "text-muted-foreground"}>{c.label}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Baseline readiness */}
          <Section icon={Database} title="Baseline readiness" testid="baseline-readiness">
            <div className="text-xs text-muted-foreground mb-3">Baseline year: <span className="font-mono text-foreground">{data.baseline_readiness.baseline_year || "—"}</span></div>
            {data.baseline_readiness.wizard_base_year && (
              <div className="text-xs mb-3 flex items-center gap-1.5" data-testid="baseline-wizard-status">
                <Check className="h-3.5 w-3.5 text-[#00E65B]" />
                <span>FY{data.baseline_readiness.wizard_base_year} baseline set in the Baseline Year Wizard{data.baseline_readiness.wizard_metrics_locked ? ` · ${data.baseline_readiness.wizard_metrics_locked} metrics locked` : ""}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {data.baseline_readiness.completed_fields.map((f) => (
                <Badge key={f} variant="success" className="gap-1"><Check className="h-3 w-3" />{f}</Badge>
              ))}
              {data.baseline_readiness.missing_fields.map((f) => (
                <Badge key={f} variant="outline" className="text-muted-foreground">{f}</Badge>
              ))}
            </div>
          </Section>

          {/* Materiality */}
          <Section icon={Target} title="Materiality setup" testid="materiality-setup">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <Stat label="Recommended" value={data.materiality_setup.recommended} />
              <Stat label="Accepted" value={data.materiality_setup.accepted} accent />
              <Stat label="High priority" value={data.materiality_setup.high_priority} />
            </div>
            <div className="space-y-1.5">
              {(data.materiality_setup.accepted_topics || []).slice(0, 5).map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`accepted-topic-${i}`}>
                  <Check className="h-3.5 w-3.5 text-accent shrink-0" />
                  <span>{t.topic}</span>
                  <Badge variant="outline" className="ml-auto">{t.dimension}</Badge>
                </div>
              ))}
            </div>
          </Section>

          {/* Compliance readiness */}
          <Section icon={ShieldCheck} title="Compliance readiness" testid="compliance-readiness">
            <div className="space-y-3">
              {data.compliance_readiness.map((c, i) => (
                <div key={i} data-testid={`compliance-${i}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>{c.framework}</span>
                    <span className="font-mono text-muted-foreground">{c.readiness_pct}% · {c.status}</span>
                  </div>
                  <Progress value={c.readiness_pct} />
                </div>
              ))}
              {data.compliance_readiness.length === 0 && <div className="text-sm text-muted-foreground">No frameworks selected yet.</div>}
            </div>
          </Section>

          {/* AI adviser */}
          <Section icon={Sparkles} title="AI adviser" testid="dashboard-adviser" noPad>
            <div className="h-[360px]">
              <AdviserPanel
                sessionId={null}
                intro="I'm your Lestari adviser. Ask me what to do next, why a setup item matters, or which package fits you best."
                prompts={data.ai_prompts}
              />
            </div>
          </Section>
        </div>

        {/* RIGHT 1/3 */}
        <div className="space-y-5">
          {/* Trial */}
          <Section icon={Clock} title="Trial status" testid="trial-status">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-4xl font-bold" data-testid="trial-days">{trial.days_remaining}</span>
              <span className="text-sm text-muted-foreground">days left</span>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <KV k="Package" v={trial.package_name} />
              <KV k="Status" v={(trial.status || "").toUpperCase()} />
              <KV k="Seats" v={trial.seats} />
              <KV k="Est. monthly" v={trial.monthly_price_sgd != null ? formatSgd(trial.monthly_price_sgd) : "—"} />
              {trial.billing_cycle && <KV k="Billing" v={trial.billing_cycle === "annual" ? "Annual · 2 mo free" : "Monthly"} />}
              {trial.trial_end && <KV k="Ends" v={new Date(trial.trial_end).toLocaleDateString("en-SG")} />}
            </div>
            <Button variant="primary" className="w-full mt-4 gap-2" onClick={() => setUpgradeOpen(true)} data-testid="open-upgrade-btn">
              <ArrowUpRight className="h-4 w-4" /> Upgrade / activate paid
            </Button>
            <SimulateTrialControl onDone={load} />
          </Section>

          {/* Verified badge */}
          <VerifiedBadgeCard slug={data.tenant.slug} name={data.tenant.name} />

          {/* Verification */}
          <Section icon={BadgeCheck} title="Verification" testid="verification-status">
            <div className="space-y-2">
              <VCheck ok={v.org_verified} label="Organisation verified" />
              <VCheck ok={v.uen_verified} label="UEN verified" />
              <VCheck ok={v.representative_verified} label="Authorised representative" />
              <VCheck ok={v.acra_synced} label="ACRA profile synced" />
            </div>
            {v.representative && (
              <div className="text-xs text-muted-foreground mt-3 border-t border-border pt-2 font-mono">
                {v.representative.name} · {v.representative.role_title}
              </div>
            )}
          </Section>

          {/* Notifications */}
          <Section icon={Mail} title="Notifications" testid="notifications-inbox" noPad>
            <div className="max-h-72 overflow-y-auto scrollbar-thin divide-y divide-border">
              {notifs.length === 0 && <div className="p-4 text-sm text-muted-foreground">No messages yet.</div>}
              {notifs.map((n) => (
                <button
                  key={n._id}
                  onClick={async () => {
                    if (!n.read) { await api.post(`/onboarding/notifications/${n._id}/read`); setNotifs((arr) => arr.map((x) => x._id === n._id ? { ...x, read: true } : x)); }
                  }}
                  className="w-full text-left p-3.5 hover:bg-surface-hover transition-colors block"
                  data-testid={`notif-${n.type}`}
                >
                  <div className="flex items-center gap-2">
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />}
                    <span className="text-sm font-medium truncate">{n.subject}</span>
                    <Badge variant="outline" className="ml-auto shrink-0">{n.type}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">{n.body}</div>
                </button>
              ))}
            </div>
          </Section>
        </div>
      </div>

      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} packages={packages} current={trial} onDone={load} />
    </div>
  );
}

function UpgradeDialog({ open, onOpenChange, packages, current, onDone }) {
  const [pkg, setPkg] = useState(null);
  const [seats, setSeats] = useState(current?.seats || 5);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setPkg(current?.package_code || "growth"); setSeats(current?.seats || 5); setCycle(current?.billing_cycle || "monthly"); } }, [open, current]);

  const selPkg = useMemo(() => packages.find((p) => p.code === pkg), [packages, pkg]);
  const total = selPkg ? (cycle === "annual" ? annualAmount(selPkg.price_sgd) : selPkg.price_sgd) * seats : null;

  const payWithStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        package_code: pkg, seats, billing_cycle: cycle, origin_url: window.location.origin,
      });
      if (data.url) { window.location.href = data.url; return; }
      toast.error("Could not start checkout.");
    } catch (e) {
      toast.error(apiErr(e, "Checkout failed"));
    } finally {
      setBusy(false);
    }
  };

  const submitSimulated = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/subscription/upgrade", { package_code: pkg, seats, billing_cycle: cycle });
      toast.success("Subscription activated (simulated).");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(apiErr(e, "Upgrade failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle>Activate a paid package</DialogTitle>
          <DialogDescription>Convert your trial to a paid subscription. Card payment is handled securely by Stripe (test mode).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex justify-center pb-1">
            <BillingCycleToggle value={cycle} onChange={setCycle} />
          </div>
          {packages.map((p) => (
            <button
              key={p.code}
              onClick={() => setPkg(p.code)}
              data-testid={`upgrade-${p.code}`}
              className={`w-full text-left border rounded-sm p-3 flex items-center gap-3 transition-colors ${pkg === p.code ? "border-accent ring-1 ring-accent bg-surface" : "border-border hover:bg-surface-hover"}`}
            >
              <div className="flex-1">
                <div className="text-sm font-semibold">{p.name}</div>
                <div className="text-xs text-muted-foreground">
                  {cycle === "annual" ? `${formatSgd(annualAmount(p.price_sgd))} / user / year` : `${formatSgd(p.price_sgd)} / user / month`}
                </div>
              </div>
              {pkg === p.code && <Check className="h-4 w-4 text-accent" />}
            </button>
          ))}
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm text-muted-foreground">Active users</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setSeats((s) => Math.max(1, s - 1))}><Minus className="h-4 w-4" /></Button>
              <Input className="w-16 text-center" type="number" value={seats} onChange={(e) => setSeats(Math.max(1, parseInt(e.target.value) || 1))} data-testid="upgrade-seats" />
              <Button variant="outline" size="icon" onClick={() => setSeats((s) => s + 1)}><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm font-medium">{cycle === "annual" ? "Annual total" : "Monthly total"}</span>
            <div className="text-right">
              <span className="font-mono text-2xl font-bold" data-testid="upgrade-total">{total != null ? formatSgd(total) : "—"}</span>
              {cycle === "annual" && <div className="text-[11px] text-accent font-mono">2 months free</div>}
            </div>
          </div>
          <Button variant="primary" className="w-full gap-2" disabled={busy || !pkg} onClick={payWithStripe} data-testid="pay-stripe-btn">
            <CreditCard className="h-4 w-4" /> {busy ? "Redirecting…" : "Pay with Stripe"}
          </Button>
          <button onClick={submitSimulated} disabled={busy || !pkg} className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="confirm-upgrade-btn">
            or activate without payment (Demo Mode)
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SimulateTrialControl({ onDone }) {
  const [day, setDay] = useState(15);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/subscription/simulate-day", { day: Math.max(1, Math.min(30, parseInt(day) || 1)) });
      toast.success(`Trial fast-forwarded to day ${day}.`);
      onDone();
    } catch (e) {
      toast.error(apiErr(e, "Could not simulate"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="simulate-trial-control">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
        <FastForward className="h-3 w-3" /> Demo: fast-forward trial
      </div>
      <div className="flex items-center gap-2">
        <Input className="w-16 text-center h-8" type="number" min={1} max={30} value={day} onChange={(e) => setDay(e.target.value)} data-testid="simulate-day-input" />
        <span className="text-xs text-muted-foreground">day of 15</span>
        <Button variant="outline" size="sm" className="ml-auto" disabled={busy} onClick={run} data-testid="simulate-day-btn">Apply</Button>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5">Try day 13 or 15 for reminders, day 16 for expiry.</p>
    </div>
  );
}

function VerifiedBadgeCard({ slug, name }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const embed = `<iframe src="${origin}/badge/${slug}" width="270" height="74" style="border:0;overflow:hidden" title="Verified by Lestari"></iframe>`;
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Copy failed"));
  };
  return (
    <Section icon={ShieldCheck} title="Verified by Lestari badge" testid="verified-badge-card">
      <div className="flex items-center gap-3 border border-border rounded-sm p-3 bg-surface mb-3">
        <div className="h-9 w-9 rounded-sm bg-accent flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-black" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.16em] text-accent font-semibold">Verified by Lestari</div>
          <div className="font-display font-bold text-[13px] leading-tight truncate">{name}</div>
        </div>
      </div>
      <div className="label-eyebrow mb-1.5">Embed code</div>
      <textarea
        readOnly
        value={embed}
        onClick={(e) => e.target.select()}
        rows={3}
        className="w-full text-[11px] font-mono bg-background border border-border rounded-sm p-2 resize-none scrollbar-thin"
        data-testid="embed-code"
      />
      <div className="flex gap-2 mt-2">
        <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => copy(embed, "Embed code")} data-testid="copy-embed-btn">
          <Code2 className="h-3.5 w-3.5" /> Copy code
        </Button>
        <a href={`/verified/${slug}`} target="_blank" rel="noopener noreferrer" className="flex-1">
          <Button variant="outline" size="sm" className="w-full gap-1.5" data-testid="view-verified-btn">
            <ExternalLink className="h-3.5 w-3.5" /> View page
          </Button>
        </a>
      </div>
    </Section>
  );
}

function Section({ icon: Icon, title, children, testid, noPad }) {
  return (
    <div className="card-flat bg-card" data-testid={testid}>
      <div className="px-5 py-3.5 border-b border-border flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-accent" />
        <h3 className="font-display font-semibold text-sm tracking-tight">{title}</h3>
      </div>
      <div className={noPad ? "" : "p-5"}>{children}</div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-border rounded-sm p-2.5 text-center">
      <div className={`font-mono text-2xl font-bold ${accent ? "text-accent" : ""}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-mono text-foreground text-right">{v ?? "—"}</span>
    </div>
  );
}

function VCheck({ ok, label }) {
  return (
    <div className="flex items-center gap-2.5 text-sm">
      {ok ? <CircleCheck className="h-4 w-4 text-accent shrink-0" /> : <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0" />}
      <span className={ok ? "" : "text-muted-foreground"}>{label}</span>
    </div>
  );
}
